import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { ProjectsService } from '../../../../../../core/projects.service';
import { SHEET_TYPES } from '../../../../../../../../shared/helpers/sheet-types';
import type { SheetDraft } from '../drawing-sheet-viewer/drawing-sheet-viewer.component';

/** The sheet viewer the drawer edits through: it holds the save, its error and the sheet it updates */
export interface SheetEditHost {
  readonly sheetDraft: SheetDraft | null;
  readonly savingSheet: boolean;
  readonly sheetError: string;
  /** "4 · PROJECT LAYOUT" under the heading */
  readonly editSheetLabel: string;
  saveSheetFrom(draft: SheetDraft): void;
  cancelEditSheet(): void;
}

/**
 * W64: Edit sheet in the right drawer (the Add record forms' drawer), not a form pushing the sheet down. The sheet stays
 * in view beside it, also in full screen. Save waits for a change (W62's rule: the form against what it opened with).
 */
@Component({
  selector: 'app-drawing-sheet-edit-panel',
  templateUrl: './drawing-sheet-edit-panel.component.html',
  styleUrls: ['./drawing-sheet-edit-panel.component.scss'],
  standalone: false,
})
export class DrawingSheetEditPanelComponent implements OnInit, OnDestroy {
  /** Set by the projects wrapper's drawer */
  data: { host: SheetEditHost };
  readonly sheetTypes = SHEET_TYPES.filter(t => t.value !== 'other');
  draft: SheetDraft = { number: '', title: '', discipline: '', sheetType: '', start: '', end: '' };
  private initial = '';
  private readonly projectsService = inject(ProjectsService);

  ngOnInit(): void {
    const start = this.data?.host?.sheetDraft;
    if (start) {
      this.draft = { ...start };
    }
    this.initial = JSON.stringify(this.draft);
  }

  ngOnDestroy(): void {
    this.projectsService.isAnyFormDirty.next(false);
  }

  get changed(): boolean {
    return JSON.stringify(this.draft) !== this.initial;
  }

  get saving(): boolean {
    return !!this.data?.host?.savingSheet;
  }

  get error(): string {
    return this.data?.host?.sheetError ?? '';
  }

  set(field: keyof SheetDraft, value: string): void {
    this.draft = { ...this.draft, [field]: value };
    // The drawer's ✕ asks before dropping a change (the app's Unsaved changes confirm)
    this.projectsService.isAnyFormDirty.next(this.changed);
  }

  save(): void {
    if (this.changed && !this.saving) {
      this.data.host.saveSheetFrom({ ...this.draft });
    }
  }

  cancel(): void {
    this.projectsService.isAnyFormDirty.next(false);
    this.data.host.cancelEditSheet();
  }
}
